import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Star } from "lucide-react";
import { toast } from "sonner";

function Stars({ rating, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onClick={() => onChange?.(i)} data-testid={`star-${i}`} className="p-0.5">
          <Star className={`w-7 h-7 ${i <= rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", area: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then(({ data }) => setReviews(data.reviews || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks for the review!");
      setForm({ name: "", area: "", rating: 5, comment: "" });
      load();
    } catch {
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="reviews-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Reviews</div>
      <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">What Jacksonville says.</h1>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {reviews.map((r, i) => (
            <div key={r.review_id || i} className="bg-white rounded-2xl border border-gray-200 p-6" data-testid={`review-${i}`}>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((j) => <Star key={j} className={`w-4 h-4 ${j <= r.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-gray-300"}`} />)}
              </div>
              <p className="mt-3 text-gray-800 leading-relaxed text-sm">“{r.comment}”</p>
              <div className="mt-4 text-sm">
                <span className="font-bold text-gray-900">{r.name}</span>
                {r.area && <span className="text-gray-500"> · {r.area}</span>}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 p-6 h-fit lg:sticky lg:top-24" data-testid="review-form">
          <h3 className="text-xl font-bold text-gray-900 font-[Outfit]">Leave a review</h3>
          <div className="mt-4 space-y-3">
            <div><Label>Name</Label><Input required className="h-11 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rev-name" /></div>
            <div><Label>Neighborhood (optional)</Label><Input className="h-11 mt-1" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="rev-area" /></div>
            <div><Label>Rating</Label><div className="mt-1.5"><Stars rating={form.rating} onChange={(v) => setForm({ ...form, rating: v })} /></div></div>
            <div><Label>Your review</Label><Textarea required className="mt-1 min-h-28" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="rev-comment" /></div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full mt-5 h-12 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-semibold" data-testid="rev-submit-btn">
            Post review
          </Button>
        </form>
      </div>
    </div>
  );
}
